import React, { useEffect, useState } from 'react'
import { GetAction } from '../../Component/GetAction'
import { PostAction } from '../../Component/PostAction'
import { PostFormAction } from '../../Component/PostFormAction'
import { RequireModule, PageHeader, BTN_PRIMARY } from './shared'
import { mediaUrl } from '../../utils/mediaUrl'
import { applyFavicon } from '../../utils/applyFavicon'

const EMPTY = {
  website_name: '',
  footer_address: '',
  footer_contact: '',
  receipt_name: '',
  receipt_address: '',
  receipt_registration_number: '',
  receipt_website: '',
  logo_path: '',
  favicon_path: '',
  show_header_name: true,
  show_header_logo: false,
  smtp_host: '',
  smtp_port: '587',
  smtp_encryption: 'tls',
  smtp_username: '',
  smtp_password: '',
  smtp_from_email: '',
  smtp_from_name: '',
  smtp_password_set: false,
  smtp_test_to: '',
}

const FAVICON_MAX_BYTES = 512 * 1024
const FAVICON_ACCEPT =
  'image/png,image/x-icon,image/vnd.microsoft.icon,.ico,.png,.svg,image/svg+xml'

const SECTION_OPEN = { website: true, receipt: true, email: true }

function SettingsSection({ id, title, hint, open, onToggle, children }) {
  const panelId = `settings-panel-${id}`
  return (
    <section className={`manage-settings-section${open ? ' is-open' : ''}`}>
      <button
        type="button"
        className="manage-settings-section-toggle"
        aria-expanded={open ? 'true' : 'false'}
        aria-controls={panelId}
        onClick={onToggle}
      >
        <span>{title}</span>
        <span className="manage-settings-section-chevron" aria-hidden="true">
          ▸
        </span>
      </button>
      <div
        id={panelId}
        className="manage-settings-section-body"
        data-settings-panel={id}
        hidden={!open}
      >
        {hint ? <p className="text-muted small">{hint}</p> : null}
        {children}
      </div>
    </section>
  )
}

function SettingsPage() {
  const [form, setForm] = useState(EMPTY)
  const [logoFile, setLogoFile] = useState(null)
  const [preview, setPreview] = useState('')
  const [faviconFile, setFaviconFile] = useState(null)
  const [faviconPreview, setFaviconPreview] = useState('')
  const [message, setMessage] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [testingMail, setTestingMail] = useState(false)
  const [open, setOpen] = useState(SECTION_OPEN)

  const toggleSection = (id) => {
    setOpen((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  const onInvalid = (e) => {
    const section = e.target.closest('[data-settings-panel]')?.getAttribute('data-settings-panel')
    if (section) {
      setOpen((prev) => (prev[section] ? prev : { ...prev, [section]: true }))
    }
  }

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      const data = await GetAction('get', {}, 'settings/getAdmin')
      if (cancelled) return
      if (data?.status === 'success' && data.response) {
        const r = data.response
        setForm({
          website_name: r.website_name || '',
          footer_address: r.footer_address || '',
          footer_contact: r.footer_contact || '',
          receipt_name: r.receipt_name || r.website_name || '',
          receipt_address: r.receipt_address || '',
          receipt_registration_number: r.receipt_registration_number || '',
          receipt_website: r.receipt_website || '',
          logo_path: r.logo_path || '',
          favicon_path: r.favicon_path || '',
          show_header_name: Number(r.show_header_name ?? 1) === 1,
          show_header_logo: Number(r.show_header_logo ?? 0) === 1,
          smtp_host: r.smtp_host || '',
          smtp_port: String(r.smtp_port || 587),
          smtp_encryption: r.smtp_encryption || 'tls',
          smtp_username: r.smtp_username || '',
          smtp_password: '',
          smtp_from_email: r.smtp_from_email || '',
          smtp_from_name: r.smtp_from_name || '',
          smtp_password_set: Number(r.smtp_password_set ?? 0) === 1,
          smtp_test_to: r.smtp_from_email || '',
        })
        setPreview(mediaUrl(r.logo_path || ''))
        setFaviconPreview(mediaUrl(r.favicon_path || ''))
        if (r.favicon_path) applyFavicon(r.favicon_path)
        if (r.website_name) document.title = r.website_name
        setMessage(null)
      } else {
        setMessage({ error: true, text: data?.message || 'Failed to load settings.' })
      }
      setLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const onChange = (e) => {
    const { name, value, type, checked } = e.target
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }))
  }

  const onLogo = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setLogoFile(file)
    setPreview(URL.createObjectURL(file))
  }

  const onFavicon = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    const name = (file.name || '').toLowerCase()
    const okExt = /\.(ico|png|svg)$/.test(name)
    if (!okExt) {
      setMessage({ error: true, text: 'Favicon must be an ICO, PNG, or SVG file.' })
      e.target.value = ''
      return
    }
    if (file.size > FAVICON_MAX_BYTES) {
      setMessage({ error: true, text: 'Favicon must be 512 KB or smaller.' })
      e.target.value = ''
      return
    }
    setMessage(null)
    setFaviconFile(file)
    setFaviconPreview(URL.createObjectURL(file))
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    setMessage(null)
    const fd = new FormData()
    fd.append('website_name', form.website_name.trim())
    fd.append('footer_address', form.footer_address.trim())
    fd.append('footer_contact', form.footer_contact.trim())
    fd.append('receipt_name', form.receipt_name.trim())
    fd.append('receipt_address', form.receipt_address.trim())
    fd.append('receipt_registration_number', form.receipt_registration_number.trim())
    fd.append('receipt_website', form.receipt_website.trim())
    fd.append('show_header_name', form.show_header_name ? '1' : '0')
    fd.append('show_header_logo', form.show_header_logo ? '1' : '0')
    fd.append('smtp_host', form.smtp_host.trim())
    fd.append('smtp_port', String(form.smtp_port || '587').trim() || '587')
    fd.append('smtp_encryption', form.smtp_encryption || 'tls')
    fd.append('smtp_username', form.smtp_username.trim())
    fd.append('smtp_from_email', form.smtp_from_email.trim())
    fd.append('smtp_from_name', form.smtp_from_name.trim())
    if (form.smtp_password.trim()) {
      fd.append('smtp_password', form.smtp_password)
    }
    if (logoFile) fd.append('logo', logoFile)
    if (faviconFile) fd.append('favicon', faviconFile)

    const data = await PostFormAction('post', fd, 'settings/save')
    if (data?.status === 'success') {
      setMessage({ error: false, text: data.message || 'Settings saved.' })
      const next = data.response || {}
      if (next.website_name) document.title = next.website_name
      if (next.favicon_path) applyFavicon(next.favicon_path)
      if (next.logo_path) setPreview(mediaUrl(next.logo_path))
      if (next.favicon_path) setFaviconPreview(mediaUrl(next.favicon_path))
      setForm((prev) => ({
        ...prev,
        ...(next.logo_path ? { logo_path: next.logo_path } : {}),
        ...(next.favicon_path ? { favicon_path: next.favicon_path } : {}),
        show_header_name: Number(next.show_header_name ?? prev.show_header_name) === 1,
        show_header_logo: Number(next.show_header_logo ?? prev.show_header_logo) === 1,
        smtp_host: next.smtp_host ?? prev.smtp_host,
        smtp_port: String((next.smtp_port ?? prev.smtp_port) || 587),
        smtp_encryption: next.smtp_encryption || prev.smtp_encryption,
        smtp_username: next.smtp_username ?? prev.smtp_username,
        smtp_from_email: next.smtp_from_email ?? prev.smtp_from_email,
        smtp_from_name: next.smtp_from_name ?? prev.smtp_from_name,
        smtp_password: '',
        smtp_password_set: Number(next.smtp_password_set ?? prev.smtp_password_set) === 1,
        smtp_test_to: prev.smtp_test_to || next.smtp_from_email || '',
      }))
      setLogoFile(null)
      setFaviconFile(null)
    } else {
      setMessage({ error: true, text: data?.message || 'Failed to save settings.' })
    }
    setSaving(false)
  }

  const onTestMail = async () => {
    setTestingMail(true)
    setMessage(null)
    const data = await PostAction(
      'post',
      { to: form.smtp_test_to.trim() },
      'settings/testMail'
    )
    if (data?.status === 'success') {
      setMessage({ error: false, text: data.message || 'Test email sent.' })
    } else {
      setMessage({
        error: true,
        text: data?.message || 'Failed to send the test email. Save SMTP settings first.',
      })
    }
    setTestingMail(false)
  }

  return (
    <RequireModule moduleKey="settings">
      <PageHeader
        title="Settings"
        subtitle="Website, donation receipt, and outgoing email."
      />
      {message ? (
        <div
          className={`manage-alert ${message.error ? 'manage-alert-err' : 'manage-alert-ok'}`}
        >
          {message.text}
        </div>
      ) : null}
      {loading ? (
        <div className="manage-card">
          <div className="text-center py-4">
            <div className="spinner-border text-warning" role="status" />
          </div>
        </div>
      ) : (
        <form onSubmit={onSubmit} onInvalid={onInvalid}>
          <SettingsSection
            id="website"
            title="Website"
            hint="Name, logo, favicon, and footer details shown on the public site."
            open={open.website}
            onToggle={() => toggleSection('website')}
          >
            <div className="mb-3">
              <label className="form-label" htmlFor="website_name">
                Website Name
              </label>
              <input
                id="website_name"
                name="website_name"
                className="form-control"
                required
                value={form.website_name}
                onChange={onChange}
              />
              <div className="form-text">Used in the footer, receipts, and browser tab title</div>
            </div>

            <div className="form-check mb-3">
              <input
                id="show_header_name"
                name="show_header_name"
                className="form-check-input"
                type="checkbox"
                checked={!!form.show_header_name}
                onChange={onChange}
              />
              <label className="form-check-label" htmlFor="show_header_name">
                Show website name in the header
              </label>
              <div className="form-text">Uncheck to hide the name from the top header.</div>
            </div>

            <div className="mb-3">
              <label className="form-label" htmlFor="logo">
                Website Logo
              </label>
              <input
                id="logo"
                type="file"
                accept="image/*"
                className="form-control"
                onChange={onLogo}
              />
              <div className="form-text">Leave empty to keep the current logo.</div>
              {preview ? (
                <img
                  src={preview}
                  alt="Logo preview"
                  className="mt-2"
                  style={{
                    maxHeight: 100,
                    maxWidth: 220,
                    objectFit: 'contain',
                    background: '#fafafa',
                    border: '1px solid #ddd',
                    padding: 8,
                    borderRadius: 4,
                  }}
                />
              ) : null}
            </div>

            <div className="form-check mb-3">
              <input
                id="show_header_logo"
                name="show_header_logo"
                className="form-check-input"
                type="checkbox"
                checked={!!form.show_header_logo}
                onChange={onChange}
              />
              <label className="form-check-label" htmlFor="show_header_logo">
                Show logo in the header
              </label>
              <div className="form-text">
                Check to show the logo next to (or instead of) the website name. Uncheck to hide it.
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label" htmlFor="favicon">
                Favicon
              </label>
              <input
                id="favicon"
                type="file"
                accept={FAVICON_ACCEPT}
                className="form-control"
                onChange={onFavicon}
              />
              <div className="form-text">
                Browser tab icon (ICO, PNG, or SVG, max 512 KB). Leave empty to keep the
                current favicon.
              </div>
              {faviconPreview ? (
                <img
                  src={faviconPreview}
                  alt="Favicon preview"
                  className="mt-2"
                  style={{
                    width: 48,
                    height: 48,
                    objectFit: 'contain',
                    background: '#fafafa',
                    border: '1px solid #ddd',
                    padding: 4,
                    borderRadius: 4,
                  }}
                />
              ) : null}
            </div>

            <div className="mb-3">
              <label className="form-label" htmlFor="footer_address">
                Footer Address
              </label>
              <textarea
                id="footer_address"
                name="footer_address"
                className="form-control"
                rows={3}
                required
                value={form.footer_address}
                onChange={onChange}
              />
            </div>

            <div className="mb-3">
              <label className="form-label" htmlFor="footer_contact">
                Footer Contact Number
              </label>
              <input
                id="footer_contact"
                name="footer_contact"
                className="form-control"
                required
                value={form.footer_contact}
                onChange={onChange}
              />
            </div>
          </SettingsSection>

          <SettingsSection
            id="receipt"
            title="Receipt"
            hint="Details printed at the top of payment receipts."
            open={open.receipt}
            onToggle={() => toggleSection('receipt')}
          >
            <div className="mb-3">
              <label className="form-label" htmlFor="receipt_name">
                Receipt Name
              </label>
              <input
                id="receipt_name"
                name="receipt_name"
                className="form-control"
                required
                value={form.receipt_name}
                onChange={onChange}
              />
            </div>

            <div className="mb-3">
              <label className="form-label" htmlFor="receipt_address">
                Receipt Address
              </label>
              <textarea
                id="receipt_address"
                name="receipt_address"
                className="form-control"
                rows={3}
                required
                value={form.receipt_address}
                onChange={onChange}
              />
            </div>

            <div className="mb-3">
              <label className="form-label" htmlFor="receipt_registration_number">
                Registration Number
              </label>
              <input
                id="receipt_registration_number"
                name="receipt_registration_number"
                className="form-control"
                required
                value={form.receipt_registration_number}
                onChange={onChange}
              />
            </div>

            <div className="mb-3">
              <label className="form-label" htmlFor="receipt_website">
                Website
              </label>
              <input
                id="receipt_website"
                name="receipt_website"
                className="form-control"
                required
                value={form.receipt_website}
                onChange={onChange}
              />
            </div>
          </SettingsSection>

          <SettingsSection
            id="email"
            title="Email settings"
            hint="Used to send password-reset emails. Leave host empty to skip SMTP. For Gmail use host smtp.gmail.com, port 587, TLS, and an app password."
            open={open.email}
            onToggle={() => toggleSection('email')}
          >
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label" htmlFor="smtp_host">
                  SMTP host
                </label>
                <input
                  id="smtp_host"
                  name="smtp_host"
                  className="form-control"
                  placeholder="smtp.gmail.com"
                  value={form.smtp_host}
                  onChange={onChange}
                  autoComplete="off"
                />
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label" htmlFor="smtp_port">
                  Port
                </label>
                <input
                  id="smtp_port"
                  name="smtp_port"
                  className="form-control"
                  type="number"
                  min="1"
                  max="65535"
                  value={form.smtp_port}
                  onChange={onChange}
                />
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label" htmlFor="smtp_encryption">
                  Encryption
                </label>
                <select
                  id="smtp_encryption"
                  name="smtp_encryption"
                  className="form-select"
                  value={form.smtp_encryption}
                  onChange={onChange}
                >
                  <option value="tls">TLS (587)</option>
                  <option value="ssl">SSL (465)</option>
                  <option value="none">None</option>
                </select>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label" htmlFor="smtp_username">
                  Username
                </label>
                <input
                  id="smtp_username"
                  name="smtp_username"
                  className="form-control"
                  value={form.smtp_username}
                  onChange={onChange}
                  autoComplete="off"
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label" htmlFor="smtp_password">
                  Password
                </label>
                <input
                  id="smtp_password"
                  name="smtp_password"
                  className="form-control"
                  type="password"
                  value={form.smtp_password}
                  onChange={onChange}
                  autoComplete="new-password"
                  placeholder={
                    form.smtp_password_set
                      ? 'Saved — leave blank to keep'
                      : 'SMTP password or app password'
                  }
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label" htmlFor="smtp_from_email">
                  From email
                </label>
                <input
                  id="smtp_from_email"
                  name="smtp_from_email"
                  className="form-control"
                  type="email"
                  value={form.smtp_from_email}
                  onChange={onChange}
                  autoComplete="off"
                />
                <div className="form-text">Required when SMTP host is set.</div>
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label" htmlFor="smtp_from_name">
                  From name
                </label>
                <input
                  id="smtp_from_name"
                  name="smtp_from_name"
                  className="form-control"
                  placeholder="Leave blank to use the website name"
                  value={form.smtp_from_name}
                  onChange={onChange}
                />
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label" htmlFor="smtp_test_to">
                Send a test email to
              </label>
              <div className="d-flex flex-wrap gap-2">
                <input
                  id="smtp_test_to"
                  name="smtp_test_to"
                  className="form-control"
                  style={{ maxWidth: 360 }}
                  type="email"
                  value={form.smtp_test_to}
                  onChange={onChange}
                  placeholder="you@example.com"
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={onTestMail}
                  disabled={testingMail || saving}
                >
                  {testingMail ? 'Sending…' : 'Send test email'}
                </button>
              </div>
              <div className="form-text">
                Save the SMTP settings first, then send a test.
              </div>
            </div>
          </SettingsSection>

          <button
            type="submit"
            className="btn btn-primary"
            style={BTN_PRIMARY}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save Settings'}
          </button>
        </form>
      )}
    </RequireModule>
  )
}

export default SettingsPage
